import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Download, FileSignature } from 'lucide-react';
import { api, download } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../../lib/ui.js';
import { EmptyState } from '../../lib/components.js';
import { formatRupees } from '@siva/shared';
import { t } from '../../i18n/index.js';

type Doc = {
  id: string;
  kind: 'payment_advice' | 'service_agreement';
  period: string;
  period_label: string;
  number: string;
  version: number;
  superseded: boolean;
  published_at: string;
  /** Only a payment advice has figures; an agreement is a document to keep. */
  net_rupees?: number;
  figures?: {
    gross_rupees: number;
    days_not_served: number;
    deduction_rupees: number;
    overtime_rupees?: number;
    tds_rupees: number;
    tds_rate_bp: number;
    tds_section: string;
    days_engaged: number;
    days_in_month: number;
    amount_in_words: string;
    bank_name: string | null;
    bank_account_number: string | null;
    /** Absent on slips issued before 18 September 2026. */
    ifsc_code?: string | null;
    pan_number?: string | null;
  };
};

/**
 * What the worker has been paid, one month at a time, and the agreement in force.
 *
 * It was every document ever issued, one under the other — three versions of August, seven
 * versions of the agreement — and the owner's word for it was "huge". A worker wants two things
 * from this screen: the slip for a month they pick, and their contract. So: the agreement in
 * force at the top, once; then a month to choose, and that month's slip. A slip that has been
 * replaced by a later version is not offered — the later one is what they were paid on.
 *
 * The figures are on the screen rather than only in the PDF. A phone — and especially the app's
 * WebView — is a poor place to open a downloaded file, and somebody checking what they were paid
 * should not have to fight a file viewer to do it. The PDF is still there for anybody who wants
 * the document itself.
 */
export function DocumentsPage() {
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [month, setMonth] = useState<string | null>(null);
  const [showing, setShowing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Where the app put the file, when it could say: "Downloads/SIVA/pay-slip-2026-08.pdf". */
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ documents: Doc[] }>('/me/documents');
      setDocs(r.documents);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
      setDocs([]);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  // The list arrives newest period first, newest version first within it.
  const agreement = useMemo(
    () => docs?.find((d) => d.kind === 'service_agreement' && !d.superseded) ?? null,
    [docs],
  );
  const slips = useMemo(() => {
    const byMonth = new Map<string, Doc>();
    for (const d of docs ?? []) {
      if (d.kind !== 'payment_advice' || d.superseded) continue;
      if (!byMonth.has(d.period)) byMonth.set(d.period, d);
    }
    return [...byMonth.values()];
  }, [docs]);
  const slip = slips.find((s) => s.period === month) ?? slips[0] ?? null;

  const save = async (d: Doc) => {
    setSaved(null);
    try {
      const stem = d.kind === 'service_agreement' ? 'employment-agreement' : 'pay-slip';
      const r = await download(`/me/documents/${d.id}.pdf`, `${stem}-${d.period}.pdf`);
      setSaved(r.saved);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (!docs) return <Spinner />;
  const f = slip?.figures;

  return (
    <div className="stack">
      <section className="card">
        <h2>{t('docs_title')}</h2>
        <p className="muted">{t('docs_sub')}</p>
        {error && <Alert>{error}</Alert>}
        {saved && <Alert kind="success">{t('docs_saved_to', { where: saved })}</Alert>}
        {slips.length === 0 ? (
          <EmptyState icon={FileText} text={t('docs_none')} />
        ) : (
          <>
            <label className="docs-month">
              {t('docs_pick_month')}
              <select
                value={slip?.period ?? ''}
                onChange={(e) => {
                  setMonth(e.target.value);
                  setShowing(false);
                }}
              >
                {slips.map((s) => (
                  <option key={s.period} value={s.period}>
                    {s.period_label}
                  </option>
                ))}
              </select>
            </label>
            {slip && (
              <div className="advice-card slip" key={slip.id}>
                <div className="row between">
                  <span>
                    <h3>{slip.period_label}</h3>
                    <small className="muted">
                      {slip.number} · {t('docs_issued')} {fmtDate(slip.published_at, false)}
                    </small>
                  </span>
                  {slip.net_rupees !== undefined && (
                    <strong className="advice-net">{formatRupees(slip.net_rupees)}</strong>
                  )}
                </div>
                {showing && f && (
                  <dl className="advice-figures">
                    <dt>{t('docs_gross')}</dt>
                    <dd>{formatRupees(f.gross_rupees)}</dd>
                    {f.deduction_rupees > 0 && (
                      <>
                        <dt>{t('docs_deduction', { n: f.days_not_served })}</dt>
                        <dd>- {formatRupees(f.deduction_rupees)}</dd>
                      </>
                    )}
                    {(f.overtime_rupees ?? 0) > 0 && (
                      <>
                        <dt>{t('docs_overtime')}</dt>
                        <dd>+ {formatRupees(f.overtime_rupees ?? 0)}</dd>
                      </>
                    )}
                    <dt>{t('docs_tds', { rate: (f.tds_rate_bp / 100).toString(), section: f.tds_section })}</dt>
                    <dd>- {formatRupees(f.tds_rupees)}</dd>
                    <dt>{t('docs_net')}</dt>
                    <dd>
                      <strong>{formatRupees(slip.net_rupees ?? 0)}</strong>
                    </dd>
                    <dt>{t('docs_words')}</dt>
                    <dd>{f.amount_in_words}</dd>
                    <dt>{t('docs_bank')}</dt>
                    <dd>
                      {f.bank_account_number
                        ? `${f.bank_name} · ${f.bank_account_number}${f.ifsc_code ? ` · IFSC ${f.ifsc_code}` : ''}`
                        : t('docs_bank_missing')}
                    </dd>
                    {f.pan_number && (
                      <>
                        <dt>{t('docs_pan')}</dt>
                        <dd>{f.pan_number}</dd>
                      </>
                    )}
                  </dl>
                )}
                <div className="row">
                  {f && (
                    <button type="button" className="secondary small" onClick={() => setShowing((v) => !v)}>
                      {showing ? t('docs_hide') : t('docs_show')}
                    </button>
                  )}
                  <button type="button" className="small" onClick={() => void save(slip)}>
                    <Download size={16} aria-hidden="true" /> {t('docs_download')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <section className="card advice-card agreement">
        <div className="row between">
          <span>
            <h3>
              <FileSignature size={18} aria-hidden="true" /> {t('docs_agreement')}
            </h3>
            {agreement ? (
              <small className="muted">
                {agreement.number} · {t('docs_issued')} {fmtDate(agreement.published_at, false)}
              </small>
            ) : null}
          </span>
        </div>
        {agreement ? (
          <>
            <p className="muted">{t('docs_agreement_sub')}</p>
            <div className="row">
              <button type="button" className="small" onClick={() => void save(agreement)}>
                <Download size={16} aria-hidden="true" /> {t('docs_download')}
              </button>
            </div>
          </>
        ) : (
          <p className="muted">{t('docs_no_agreement')}</p>
        )}
      </section>
    </div>
  );
}
